import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

import { ArrowRight, ChevronDown } from 'lucide-react';

import { useSelector } from 'react-redux';

import { cn, formatCurrency } from '@client/lib';
import { selectClient, selectClientCalculationSettings } from '@client/model';
import { insuranceCarrierDefs, InsuranceCarrier, carePhaseDefs } from '@shared';
import { normalizePoliciesForComparison } from '@client/lib/normalizePolicyForComparison';

/**
 * Get carrier image by carrierId enum value
 */
function getCarrierImage(carrierId?: number): { src: string; size: string } | undefined {
  if (carrierId !== undefined && insuranceCarrierDefs[carrierId as InsuranceCarrier]?.image) {
    return insuranceCarrierDefs[carrierId as InsuranceCarrier].image;
  }
  return undefined;
}

export function PoliciesComparisonModal({ children }: { children: React.ReactNode }) {
  const client = useSelector(selectClient);
  const [showMoreDetails, setShowMoreDetails] = useState(false);

  const ltcPolicies = client.multipleFundingSources.ltcPolicy;
  const data = normalizePoliciesForComparison(ltcPolicies, client);

  // Determine the label for inflated benefits based on whether client has started LTC
  const clientHasStartedLtc = client?.intakeSurvey?.clientHasStartedLtc;
  const clientCurrentCarePhase = client?.intakeSurvey?.clientCurrentCarePhase;
  const inflatedBenefitLabel =
    clientHasStartedLtc && clientCurrentCarePhase
      ? `At ${carePhaseDefs[clientCurrentCarePhase].label} Phase`
      : 'At LTC start';

  if (!data || data.length === 0) {
    return (
      <Dialog>
        <DialogTrigger asChild>{children}</DialogTrigger>
        <DialogContent className="w-full max-w-[95%] !rounded-3xl !px-0">
          <DialogHeader className="px-8 text-start">
            <DialogTitle className="text-primary-dark text-2xl">Policy Comparison</DialogTitle>
          </DialogHeader>
          <div className="px-8 py-6">
            <p className="text-gray-600">
              No valid policies found to compare. Please ensure policies have the required data.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="grid max-h-[95dvh] w-full max-w-[95vw] grid-rows-[auto,1fr] gap-3 overflow-hidden rounded-2xl !px-0 !pb-0 !pt-3 md:gap-4 md:!rounded-3xl md:!pt-6">
        <DialogHeader className="px-4 text-start md:px-8">
          <DialogTitle className="text-primary-dark text-lg md:text-2xl">
            Policy Comparison
          </DialogTitle>
        </DialogHeader>
        <div className="relative flex flex-col overflow-hidden pb-3 pl-0 md:mt-2 md:rounded-l-2xl md:pb-8 md:pl-2">
          <div className="absolute left-0 top-0 z-10 hidden h-full w-16 bg-white sm:block" />
          <div className="absolute left-8 top-0 z-10 hidden h-[calc(100%-32px)] w-64 max-w-64 rounded-2xl bg-white shadow-2xl shadow-black/10 md:block md:w-[340px] md:max-w-[340px]" />
          <div className="relative flex overflow-y-auto">
            <div className="overflow-x-auto [-ms-overflow-style:'none'] [scrollbar-width:'none'] [&::-webkit-scrollbar]:hidden">
              <table className="w-max table-fixed border-separate border-spacing-x-3 md:border-spacing-x-6">
                <thead>
                  <tr>
                    <ThSticky>Solution Provider</ThSticky>
                    {data.map(policy => {
                      const carrierImage = getCarrierImage(policy.carrierId);
                      return (
                        <Th
                          key={policy.fundingSourceId}
                          title={policy.productName ?? 'Insurance'}
                          img={carrierImage?.src}
                          imgClassName={carrierImage?.size}
                        />
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  <tr className="align-top">
                    <Td
                      isSticky
                      title="Monthly Benefit"
                      className="hidden sm:table-cell"
                    >
                      <ul className="text-primary-dark mt-2 space-y-2 pl-1 text-xs md:mt-4 md:text-sm">
                        <li>Day One</li>
                        <li>{inflatedBenefitLabel}</li>
                      </ul>
                    </Td>
                    {data.map(policy => (
                      <Td
                        key={policy.fundingSourceId}
                        header="Monthly Benefit"
                      >
                        <ul className="text-primary-dark space-y-2 text-sm font-medium md:text-base">
                          <li>
                            <span className="inline-block text-xs text-gray-600 sm:hidden">
                              Day One:&nbsp;
                            </span>
                            <span>{policy.initialMonthlyBenefit}</span>
                          </li>
                          <li>
                            <span className="inline-block text-xs text-gray-600 sm:hidden">
                              {inflatedBenefitLabel}:&nbsp;
                            </span>
                            <span>{policy.inflatedMonthlyBenefit}</span>
                          </li>
                        </ul>
                      </Td>
                    ))}
                  </tr>
                  <tr className="align-top">
                    <Td
                      isSticky
                      title="Total Benefit"
                      className="hidden sm:table-cell"
                    >
                      <ul className="text-primary-dark mt-2 space-y-2 pl-1 text-xs md:mt-4 md:text-sm">
                        <li>Day One</li>
                        <li>{inflatedBenefitLabel}</li>
                      </ul>
                    </Td>
                    {data.map(policy => (
                      <Td
                        key={policy.fundingSourceId}
                        header="Total Benefit"
                      >
                        <ul className="text-primary-dark space-y-2 text-sm font-medium md:text-base">
                          <li>
                            <span className="inline-block text-xs text-gray-600 sm:hidden">
                              Day One:&nbsp;
                            </span>
                            <span>{policy.initialTotalBenefit}</span>
                          </li>
                          <li>
                            <span className="inline-block text-xs text-gray-600 sm:hidden">
                              {inflatedBenefitLabel}:&nbsp;
                            </span>
                            <span>{policy.inflatedTotalBenefit}</span>
                          </li>
                        </ul>
                      </Td>
                    ))}
                  </tr>
                  <tr className="align-top">
                    <Td
                      isSticky
                      title="Growth"
                      className="hidden sm:table-cell"
                    />
                    {data.map(policy => (
                      <Td
                        key={policy.fundingSourceId}
                        title={policy.inflationDescription ?? '-'}
                        header="Growth"
                      />
                    ))}
                  </tr>
                  {/* <tr className="align-top">
                    <Td
                      isSticky
                      title="Benefit Period"
                      className="hidden sm:table-cell"
                    />
                    {data.map(policy => (
                      <Td
                        key={policy.fundingSourceId}
                        title={
                          policy.benefitPeriodMonths ? `${policy.benefitPeriodMonths} Months` : '-'
                        }
                        header="Benefit Period"
                      />
                    ))}
                  </tr> */}
                  {showMoreDetails && (
                    <>
                      <tr className="align-top">
                        <Td
                          isSticky
                          title="Elimination Period"
                          className="hidden sm:table-cell"
                        />
                        {data.map(policy => (
                          <Td
                            key={policy.fundingSourceId}
                            title={policy.eliminationPeriodLabel ?? '-'}
                            header="Elimination Period"
                          />
                        ))}
                      </tr>
                      <tr className="align-top">
                        <Td
                          isSticky
                          title="Policy Type"
                          className="hidden sm:table-cell"
                        />
                        {data.map(policy => (
                          <Td
                            key={policy.fundingSourceId}
                            title={policy.productDescription ?? '-'}
                            header="Policy Type"
                          />
                        ))}
                      </tr>
                      <tr className="align-top">
                        <Td
                          isSticky
                          title="Method Benefits Paid"
                          className="hidden sm:table-cell"
                        />
                        {data.map(policy => (
                          <Td
                            key={policy.fundingSourceId}
                            title={policy.paymentMethod ?? '-'}
                            header="Method Benefits Paid"
                          />
                        ))}
                      </tr>
                    </>
                  )}
                  <tr className="align-top">
                    <td className="border-primary/15 z-10 hidden border-x bg-white p-3 sm:sticky sm:left-3 sm:table-cell md:left-6 md:p-6">
                      <button
                        className="text-primary flex items-center gap-2 text-sm font-semibold"
                        onClick={() => setShowMoreDetails(prev => !prev)}
                      >
                        {showMoreDetails ? 'Less Details' : 'More Details'}
                        <ChevronDown
                          className={cn('size-4', {
                            'rotate-180': showMoreDetails,
                          })}
                        />
                      </button>
                    </td>
                    {data.map(policy => (
                      <td
                        key={policy.fundingSourceId}
                        className="border-primary/15 border-x p-3 md:p-6"
                      >
                        <button
                          className="text-primary flex w-full items-center justify-center gap-2 text-center text-sm font-semibold sm:hidden"
                          onClick={() => setShowMoreDetails(prev => !prev)}
                        >
                          {showMoreDetails ? 'Less Details' : 'More Details'}
                          <ChevronDown
                            className={cn('size-4', {
                              'rotate-180': showMoreDetails,
                            })}
                          />
                        </button>
                      </td>
                    ))}
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td className="border-primary/15 text-primary-dark z-20 hidden w-64 max-w-64 rounded-b-xl border bg-white p-3 text-left sm:sticky sm:left-3 sm:table-cell md:left-6 md:w-[340px] md:max-w-[340px] md:rounded-b-2xl md:p-6">
                      <div className="text-sm font-medium md:text-base">Premium</div>
                    </td>
                    {data.map(policy => (
                      <Tf key={policy.fundingSourceId}>
                        {(policy.premiumMonthlyCost ?? 0) > 0 ? (
                          <div className="text-sm md:text-base">
                            <strong>{formatCurrency(policy.premiumMonthlyCost)}/mo</strong> -
                            Monthly Premium
                          </div>
                        ) : null}
                        {(policy.lumpSumPayment ?? 0) > 0 ? (
                          <div className="text-sm md:text-base">
                            <strong>{formatCurrency(policy.lumpSumPayment)}</strong> - Single
                            Premium
                          </div>
                        ) : null}
                        {!(policy.premiumMonthlyCost ?? 0) && !(policy.lumpSumPayment ?? 0) && (
                          <div className="text-sm md:text-base">-</div>
                        )}
                      </Tf>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ThSticky({ children }: { children: React.ReactNode }) {
  return (
    <th className="border-primary/15 top-0 z-50 hidden w-64 max-w-64 rounded-t-xl border bg-white p-3 text-left sm:sticky sm:left-3 sm:table-cell md:left-6 md:w-[340px] md:max-w-[340px] md:rounded-t-2xl md:p-6">
      <div className="text-primary-dark flex items-center justify-between">
        <div className="text-base font-medium md:text-lg">{children}</div>
        <ArrowRight className="size-4 md:size-5" />
      </div>
    </th>
  );
}

function Th({
  title,
  img,
  className,
  imgClassName,
}: {
  title: string;
  img?: string;
  className?: string;
  imgClassName?: string;
}) {
  return (
    <th
      className={cn(
        'border-primary/15 sticky top-0 z-[9] h-full w-64 max-w-64 overflow-hidden rounded-t-xl border bg-white p-0 align-top md:w-[340px] md:max-w-[340px] md:rounded-t-2xl',
        className,
      )}
    >
      <div className="flex h-24 flex-col md:h-28">
        <div className="bg-primary/10 text-primary-dark py-1 text-center text-xs font-medium md:py-2">
          {title}
        </div>
        <div className="grid flex-1 place-items-center px-3 py-3 md:px-16 md:py-4">
          {img ? (
            <img
              src={img}
              alt={title}
              className={cn('mx-auto h-auto w-28 md:w-32', imgClassName)}
            />
          ) : (
            <div className="text-primary-dark text-balance py-1.5 text-lg font-semibold md:text-xl">
              {title}
            </div>
          )}
        </div>
      </div>
    </th>
  );
}

function Td({
  title,
  children,
  isSticky,
  header,
  className,
}: {
  title?: string;
  children?: React.ReactNode;
  isSticky?: boolean;
  header?: string;
  className?: string;
}) {
  return (
    <td
      className={cn(
        'border-primary/15 relative border-x border-b bg-white px-3 py-3 align-middle md:px-6 md:py-4',
        className,
        {
          'z-10 sm:sticky sm:left-3 md:left-6': isSticky,
        },
      )}
    >
      <div
        className={cn('text-center', {
          'text-start': isSticky,
        })}
      >
        <div
          className={cn('text-primary-dark text-balance text-sm font-medium md:text-base', {
            'pt-[22px] sm:pt-0': header,
            'hidden sm:block': !title,
          })}
        >
          {title ?? <>&nbsp;</>}
        </div>
        {header && (
          <div className="absolute left-0 top-0 block w-full bg-gray-100 py-1 text-center text-xs font-medium text-gray-600 sm:hidden md:py-2">
            {header}
          </div>
        )}
        {children && (
          <div
            className={cn({
              'pt-[22px] sm:pt-0': header,
            })}
          >
            {children}
          </div>
        )}
      </div>
    </td>
  );
}

function Tf({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <td
      className={cn(
        'border-primary/15 bg-primary/10 rounded-b-xl border p-3 md:rounded-b-2xl md:p-6',
        className,
      )}
    >
      <div className="text-primary-dark space-y-2 text-center">{children}</div>
    </td>
  );
}
